import { NumberInput } from "@/components/admin";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

// Two-tier quotation: when Sales sets a markup %, auto-fill each line's
// Customer Price = Cost × (1 + markup/100). Runs only when the markup value
// itself changes, so per-line manual overrides afterwards are preserved.
const MarkupApplier = () => {
  const { setValue, getValues } = useFormContext();
  const markup = useWatch({ name: "markup_percentage" });
  const last = useRef<unknown>(undefined);

  useEffect(() => {
    if (markup === undefined || markup === null || markup === "") return;
    if (last.current === markup) return;
    last.current = markup;
    const pct = Number(markup);
    if (Number.isNaN(pct)) return;
    const items = (getValues("items") as any[]) || [];
    items.forEach((it, i) => {
      const cost = Number(it?.cost_price) || 0;
      if (cost > 0) {
        const price = Math.round(cost * (1 + pct / 100) * 100) / 100;
        setValue(`items.${i}.unit_price`, price, { shouldDirty: true });
      }
    });
  }, [markup, setValue, getValues]);

  return null;
};

// Header markup control for the two-tier quotation. Operations fill per-line
// Cost; Sales enter a markup % here to derive customer prices. Laid out as a
// standard labelled field (matching the rest of the form) rather than a loud
// stand-alone banner (ID:1).
export const QuotationMarkupField = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start bg-muted p-4 rounded-lg border">
    <div className="w-full">
      <div className="text-xs font-bold text-muted-foreground mb-1.5">Sales Markup %</div>
      <NumberInput
        source="markup_percentage"
        label={false}
        placeholder="e.g. 20"
        helperText={false}
        className="w-full !mt-0"
      />
    </div>
    <p className="md:col-span-2 text-xs text-muted-foreground leading-snug self-center">
      Auto-fills each line's <strong>Customer Price = Cost × (1 + markup%)</strong>.
      You can still edit any line's price manually afterwards. Cost &amp; margin are
      internal — they never appear on the customer PDF.
    </p>
    <MarkupApplier />
  </div>
);
